import { z } from "zod";

/** The two read-only EVM networks supported by this Flurry build. */
export const Chain = z.enum(["avalanche", "robinhood"]);
export type Chain = z.infer<typeof Chain>;

const EVM_ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/;

/** Both supported networks use canonical 20-byte EVM addresses. */
export function isValidAddress(_chain: Chain, address: string): boolean {
  return EVM_ADDRESS_RE.test(address);
}

/**
 * Coverage is program-oriented, not brand-oriented: most "launchpads" are skins
 * on shared infrastructure (LetsBonk et al. on Raydium LaunchLab; Believe/Bags
 * on Meteora DBC). One decoder per program covers every skin on it.
 */
export const LaunchProgram = z.enum(["AVALANCHE_LFJ", "POOLS_TRADE"]);
export type LaunchProgram = z.infer<typeof LaunchProgram>;

export const EvidenceStatus = z.enum(["verified", "degraded", "unavailable"]);
export type EvidenceStatus = z.infer<typeof EvidenceStatus>;

export const DEFAULT_RISK_EVIDENCE = {
  blockActivityStatus: "unavailable",
  fundingLineageStatus: "unavailable",
  creatorHistoryStatus: "unavailable",
  rugHistoryStatus: "unavailable",
  creatorHoldingsStatus: "unavailable",
  blockWallets: 0,
  blockSupplyPct: 0,
  linkedWallets: 0,
  creatorPriorListings: 0,
  creatorPriorRugs: 0,
  creatorHoldingsPct: 0,
} as const;

/**
 * Per-signal evidence for the interval risk model. Zero values are meaningful
 * only when the corresponding status is verified; otherwise they remain unknown.
 */
export const RiskEvidence = z.object({
  blockActivityStatus: EvidenceStatus,
  fundingLineageStatus: EvidenceStatus,
  creatorHistoryStatus: EvidenceStatus,
  rugHistoryStatus: EvidenceStatus,
  creatorHoldingsStatus: EvidenceStatus,
  blockWallets: z.number().int().nonnegative(),
  blockSupplyPct: z.number().min(0).max(100),
  linkedWallets: z.number().int().nonnegative(),
  creatorPriorListings: z.number().int().nonnegative(),
  creatorPriorRugs: z.number().int().nonnegative(),
  creatorHoldingsPct: z.number().min(0).max(100),
});
export type RiskEvidence = z.infer<typeof RiskEvidence>;

export const LifecycleKind = z.enum(["direct-pool", "candidate"]);
export type LifecycleKind = z.infer<typeof LifecycleKind>;

/** A wallet's activity in/around the deploy slot of a token. */
export const SlotActivity = z.object({
  wallet: z.string().min(1),
  /** EVM block number — an opaque listing/deploy unit to forensics. */
  slot: z.number().int().nonnegative(),
  supplyPct: z.number().min(0).max(100),
  fundedBy: z.string().min(1).optional(),
});
export type SlotActivity = z.infer<typeof SlotActivity>;

export const Launch = z
  .object({
    chain: Chain.default("avalanche"),
    mint: z.string(),
    ticker: z.string(),
    name: z.string(),
    program: LaunchProgram,
    /** Display name of the launchpad skin (e.g. "PUMP.FUN", "LETSBONK", "BELIEVE"). */
    platformLabel: z.string(),
    deployer: z.string(),
    /** EVM block number — see SlotActivity.slot. */
    deploySlot: z.number().int().nonnegative(),
    launchedAt: z.number(), // unix ms
    mcapUsd: z.number().nonnegative(),
    devHoldsPct: z.number().min(0).max(100),
    deployerPriorLaunches: z.number().int().nonnegative(),
    deployerPriorRugs: z.number().int().nonnegative(),
    /** False on any provider that cannot verify prior-rug history client-side (see DECODING.md). */
    rugHistoryVerified: z.boolean(),
    slotActivity: z.array(SlotActivity),
    lifecycle: LifecycleKind.default("direct-pool"),
    listingStatus: EvidenceStatus.default("verified"),
    marketDataStatus: EvidenceStatus.default("unavailable"),
    forensicsStatus: EvidenceStatus.default("unavailable"),
    riskEvidence: RiskEvidence.default(DEFAULT_RISK_EVIDENCE),
    poolAddress: z.string().optional(),
    proofTxHash: z.string().optional(),
  })
  .superRefine((val, ctx) => {
    if (!isValidAddress(val.chain, val.mint)) {
      ctx.addIssue({
        code: "custom",
        path: ["mint"],
        message: `mint is not a valid ${val.chain} address`,
      });
    }
    if (!isValidAddress(val.chain, val.deployer)) {
      ctx.addIssue({
        code: "custom",
        path: ["deployer"],
        message: `deployer is not a valid ${val.chain} address`,
      });
    }
    val.slotActivity.forEach((a, i) => {
      if (!isValidAddress(val.chain, a.wallet)) {
        ctx.addIssue({
          code: "custom",
          path: ["slotActivity", i, "wallet"],
          message: `wallet is not a valid ${val.chain} address`,
        });
      }
      if (a.fundedBy !== undefined && !isValidAddress(val.chain, a.fundedBy)) {
        ctx.addIssue({
          code: "custom",
          path: ["slotActivity", i, "fundedBy"],
          message: `fundedBy is not a valid ${val.chain} address`,
        });
      }
    });
  });
export type Launch = z.infer<typeof Launch>;

export const GraduationEntry = z
  .object({
    chain: Chain.default("avalanche"),
    mint: z.string(),
    ticker: z.string(),
    program: LaunchProgram,
    platformLabel: z.string(),
    curveProgressPct: z.number().min(0).max(100),
    mcapUsd: z.number().nonnegative(),
    vol1hUsd: z.number().nonnegative(),
    holders: z.number().int().nonnegative(),
    /** False on any provider that cannot verify vol1hUsd/holders client-side (see DECODING.md). */
    volHoldersVerified: z.boolean(),
    pinned: z.boolean().default(false),
    lifecycle: LifecycleKind.default("direct-pool"),
    listingStatus: EvidenceStatus.default("verified"),
    marketDataStatus: EvidenceStatus.default("unavailable"),
    riskEvidence: RiskEvidence.default(DEFAULT_RISK_EVIDENCE),
  })
  .superRefine((val, ctx) => {
    if (!isValidAddress(val.chain, val.mint)) {
      ctx.addIssue({
        code: "custom",
        path: ["mint"],
        message: `mint is not a valid ${val.chain} address`,
      });
    }
  });
export type GraduationEntry = z.infer<typeof GraduationEntry>;

/** Structured evidence handed to the AI dossier. Nothing else crosses that boundary. */
export const DossierEvidence = z.object({
  chain: Chain,
  ticker: z.string(),
  platformLabel: z.string(),
  deployer: z.string(),
  bundled: z.boolean(),
  bundleWallets: z.number().int().nonnegative(),
  firstBlockSupplyPct: z.number().min(0).max(100),
  linkedWallets: z.number().int().nonnegative(),
  deployerPriorLaunches: z.number().int().nonnegative(),
  deployerPriorRugs: z.number().int().nonnegative(),
  devHoldsPct: z.number().min(0).max(100),
  listingStatus: EvidenceStatus,
  forensicsStatus: EvidenceStatus,
  marketDataStatus: EvidenceStatus,
});
export type DossierEvidence = z.infer<typeof DossierEvidence>;
